import type { Risk, RiskCategory, RiskModelType } from "@unbox/db";
import { Edit01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type CreateRiskInput, useCreateRisk, useUpdateRisk } from "@/api/admin/risks";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { TextArea } from "@/components/base/textarea/textarea";
import { CATEGORY_LABELS, tierToSeverityLabel } from "@/components/safety-config/risk-groups";
import {
  buildConfigFromFields,
  CATALOG_CONFIG_FIELDS,
  defaultConfigFromFields,
  MODEL_TYPE_LABELS,
  slugifyRiskId,
} from "@/pages/admin/merchant-detail/tabs/risk-config-v2/config-fields";
import { RiskConfigEditor } from "@/pages/admin/merchant-detail/tabs/risk-config-v2/RiskConfigEditor";
import { error, success } from "@/providers/ToastProvider";

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABELS) as RiskCategory[]).map((c) => ({
  id: c,
  label: CATEGORY_LABELS[c],
}));
const MODEL_TYPE_OPTIONS = (Object.keys(MODEL_TYPE_LABELS) as RiskModelType[]).map((m) => ({
  id: m,
  label: MODEL_TYPE_LABELS[m],
}));
const TIER_OPTIONS = [1, 2].map((t) => ({ id: String(t), label: tierToSeverityLabel(t) }));

type FormState = {
  id: string;
  readableName: string;
  readableDescription: string;
  category: RiskCategory;
  modelType: RiskModelType;
  defaultTier: number;
  vision: boolean;
  config: Record<string, unknown>;
};

const buildInitial = (risk?: Risk): FormState => ({
  id: risk?.id ?? "",
  readableName: risk?.readableName ?? "",
  readableDescription: risk?.readableDescription ?? "",
  category: (risk?.category as RiskCategory) ?? "baseline",
  modelType: (risk?.modelType as RiskModelType) ?? "debate",
  defaultTier: risk?.defaultTier ?? 1,
  vision: risk?.vision ?? false,
  config: buildConfigFromFields(
    CATALOG_CONFIG_FIELDS[(risk?.modelType as RiskModelType) ?? "debate"],
    risk?.config as Record<string, unknown> | null,
  ),
});

export const CreateRiskModal = () => {
  const [open, setOpen] = useState(false);
  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button>Add risk</Button>
      <RiskModalContent mode="create" open={open} onClose={() => setOpen(false)} />
    </DialogTrigger>
  );
};

export const EditRiskModal = ({ risk }: { risk: Risk }) => {
  const [open, setOpen] = useState(false);
  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Edit" icon={Edit01} onClick={() => setOpen(true)} />
      <RiskModalContent mode="edit" open={open} onClose={() => setOpen(false)} risk={risk} />
    </DialogTrigger>
  );
};

type ModalContentProps =
  | { mode: "create"; open: boolean; onClose: () => void; risk?: undefined }
  | { mode: "edit"; open: boolean; onClose: () => void; risk: Risk };

const RiskModalContent = ({ mode, open, onClose, risk }: ModalContentProps) => {
  const isEdit = mode === "edit";
  const [state, setState] = useState<FormState>(() => buildInitial(risk));
  // Once the admin edits the id by hand, stop auto-deriving it from the name.
  const [idEdited, setIdEdited] = useState(false);

  const { mutateAsync: create, isPending: isCreating } = useCreateRisk();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateRisk();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset form on open
  useEffect(() => {
    setState(buildInitial(risk));
    setIdEdited(false);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const handleNameChange = (name: string) =>
    setState((prev) => ({
      ...prev,
      readableName: name,
      // Auto-fill the id from the name until it's been manually edited (create only).
      id: !isEdit && !idEdited ? slugifyRiskId(name) : prev.id,
    }));

  const handleIdChange = (value: string) => {
    setIdEdited(true);
    set("id", value);
  };

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await update({
          id: risk.id,
          patch: {
            readableName: state.readableName,
            readableDescription: state.readableDescription,
            category: state.category,
            defaultTier: state.defaultTier,
            vision: state.vision,
            config: state.config,
          },
        });
        success("Risk updated");
      } else {
        if (!state.id.trim() || !state.readableName.trim()) {
          error("ID and name are required");
          return;
        }
        const payload: CreateRiskInput = {
          id: state.id.trim(),
          readableName: state.readableName,
          readableDescription: state.readableDescription,
          category: state.category,
          modelType: state.modelType,
          defaultTier: state.defaultTier,
          vision: state.vision,
          config: state.config,
        };
        await create(payload);
        success("Risk created");
      }
      onClose();
    } catch (e) {
      error(e instanceof Error ? e.message : "Failed to save risk");
    }
  };

  return (
    <ModalOverlay isDismissable>
      <Modal className="dark-mode">
        <Dialog>
          <div className="min-w-[600px] max-w-[600px] relative w-full overflow-hidden rounded-2xl bg-primary shadow-none">
            <CloseButton onClick={onClose} theme="light" size="lg" className="absolute top-3 right-3" />
            <div className="flex flex-col gap-0.5 px-6 pt-6">
              <Heading slot="title" className="text-md font-semibold text-primary">
                {isEdit ? `Edit risk: ${risk.readableName}` : "Add risk"}
              </Heading>
              <p className="text-sm text-tertiary">
                {isEdit ? "Model type and ID cannot be changed after creation." : "Define a new risk in the catalog."}
              </p>
            </div>

            <div className="flex flex-col gap-4 px-6 py-6 max-h-[70vh] overflow-y-auto">
              <Input
                label="Readable name"
                placeholder="e.g. Banned competitor mentions"
                value={state.readableName}
                onChange={handleNameChange}
                isRequired
              />
              <Input
                label="ID"
                placeholder="e.g. banned_competitor_mentions"
                value={state.id}
                onChange={handleIdChange}
                isDisabled={isEdit}
                hint={isEdit ? undefined : "Auto-filled from the name; edit to override. Cannot be changed later."}
              />
              <TextArea
                label="User facing description"
                placeholder="What this risk detects…"
                value={state.readableDescription}
                onChange={(v: string) => set("readableDescription", v)}
                rows={2}
                isRequired
              />
              <Select
                label="Category"
                items={CATEGORY_OPTIONS}
                selectedKey={state.category}
                onSelectionChange={(k) => set("category", k as RiskCategory)}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
              <Select
                label="Model type"
                items={MODEL_TYPE_OPTIONS}
                selectedKey={state.modelType}
                isDisabled={isEdit}
                onSelectionChange={(k) =>
                  setState((prev) => ({
                    ...prev,
                    modelType: k as RiskModelType,
                    config: defaultConfigFromFields(CATALOG_CONFIG_FIELDS[k as RiskModelType]),
                  }))
                }
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
              <Select
                label="Default severity"
                items={TIER_OPTIONS}
                selectedKey={String(state.defaultTier)}
                onSelectionChange={(k) => set("defaultTier", Number(k))}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
              <Checkbox
                isSelected={state.vision}
                onChange={(checked) => set("vision", checked)}
                size="sm"
                label="Vision enabled"
                hint="Whether this risk inspects visual content."
              />
              <div className="flex flex-col gap-2 border-t border-primary pt-4">
                <span className="text-sm font-medium text-primary">Default config</span>
                <RiskConfigEditor
                  fields={CATALOG_CONFIG_FIELDS[state.modelType]}
                  config={state.config}
                  onChange={(config) => set("config", config)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-primary">
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} isLoading={isCreating || isUpdating}>
                {isEdit ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
